"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const CONSENT_COOKIE = "granbay.cookie_consent";
const CONSENT_TTL_DAYS = 365;

/**
 * EPIC-050 STORY-2211 — cookie consent banner.
 *
 * Shows once per browser until the user accepts or rejects. Honors
 * the locked jurisdiction-scoping rule (CLAUDE.md 2026-05-03) — the
 * Brazilian variant frames the choice under LGPD; the default English
 * variant uses generic industry-standard wording with NO LGPD
 * reference. Other locales fall back to the English variant until
 * their respective markets activate.
 *
 * Phase 1 scope: a thin banner with Accept / Reject + a link to the
 * Privacy Policy. Granular per-category controls (necessary /
 * preferences / analytics / marketing) land when STORY-2211 phase 2
 * scopes the cookie taxonomy in cookie-banner copy.
 *
 * Reject behaviour today is a noop (we don't load any analytics or
 * marketing cookies pre-Gate-A) — the banner records the choice for
 * future cookie deployments.
 */
export function CookieConsentBanner() {
  const { t, i18n } = useTranslation();
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const has = document.cookie
      .split(";")
      .some((part) => part.trim().startsWith(`${CONSENT_COOKIE}=`));
    if (!has) setVisible(true);
  }, []);

  // v2 redesign (2026-05-21) — suppress the v1 banner on /v2 surfaces
  // so it doesn't stack with the BetaBanner / overlap the V2Footer.
  // A v2-styled cookie banner will land before /v2 promotes to /.
  if (pathname?.startsWith("/v2")) return null;
  if (pathname?.startsWith("/v3")) return null;
  if (pathname?.startsWith("/v4")) return null;
  if (pathname?.startsWith("/v5")) return null;
  if (pathname?.startsWith("/v6")) return null;

  if (!visible) return null;

  function record(value: "accepted" | "rejected") {
    const maxAge = CONSENT_TTL_DAYS * 24 * 60 * 60;
    document.cookie = `${CONSENT_COOKIE}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
    setVisible(false);
  }

  const isPt = i18n.language === "pt" || i18n.language === "pt-BR";
  // The body text is localized; the Brazilian variant explicitly
  // names LGPD as the legal basis, the default English variant uses
  // generic phrasing that doesn't reference any specific jurisdiction's
  // framework. Falls back to a hardcoded default if the i18n catalog
  // hasn't shipped the keys yet (which it hasn't in this PR — keys are
  // wired in phase 2 once the copy is locked).
  const body = isPt
    ? t(
        "cookieConsent.body.pt",
        "Usamos cookies essenciais para operar a Granbay (autenticação, preferências de exibição). Em conformidade com a LGPD, pedimos seu consentimento antes de habilitar cookies não essenciais (análise, marketing) caso sejam introduzidos no futuro.",
      )
    : t(
        "cookieConsent.body.en",
        "We use essential cookies to operate Granbay (authentication, display preferences). With your consent, we may enable optional cookies for analytics or marketing if introduced in the future.",
      );

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookieConsent.label", "Cookie consent")}
      className="fixed bottom-0 left-0 right-0 z-[60] border-t backdrop-blur-md md:bottom-3 md:left-1/2 md:right-auto md:max-w-[640px] md:-translate-x-1/2 md:rounded-md md:border"
      style={{
        borderColor: "var(--color-border)",
        background: "color-mix(in srgb, var(--color-bg) 92%, transparent)",
      }}
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-5 py-3 text-[12px] text-[var(--color-text2)] md:flex-row md:items-center md:justify-between">
        <p className="leading-snug">
          {body}{" "}
          <Link
            href="/legal/privacy"
            className="underline hover:text-[var(--color-text)]"
          >
            {t("cookieConsent.policyLink", "Privacy Policy")}
          </Link>
        </p>
        <div className="flex flex-shrink-0 gap-2">
          <button
            type="button"
            onClick={() => record("rejected")}
            className="rounded-md border px-3 py-1.5 text-[11px] font-bold transition-colors"
            style={{
              borderColor: "var(--color-border)",
              color: "var(--color-text2)",
            }}
          >
            {t("cookieConsent.reject", "Reject")}
          </button>
          <button
            type="button"
            onClick={() => record("accepted")}
            className="rounded-md px-3 py-1.5 text-[11px] font-bold text-white"
            style={{ background: "var(--color-brand)" }}
          >
            {t("cookieConsent.accept", "Accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
